/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "SSH Tunneling", description = "Hightouch supports self-service SSH tunneling, allowing you to securely connect to your data warehouse.", lastUpdatedDate = new Date(1748563200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "SSH Tunneling",
        description: "Hightouch supports self-service SSH tunneling, allowing you to securely connect to your data warehouse.",
        lastUpdatedDate: "Fri May 30 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Sources",
          "depth": 1,
          "id": "sources"
        }, {
          "value": "Event Sources",
          "depth": 1,
          "id": "event-sources"
        }, {
          "value": "Destinations",
          "depth": 1,
          "id": "destinations"
        }, {
          "value": "Event Destinations",
          "depth": 1,
          "id": "event-destinations"
        }, {
          "value": "Standard versus reverse tunneling",
          "depth": 1,
          "id": "standard-versus-reverse-tunneling"
        }, {
          "value": "Standard tunneling",
          "depth": 0,
          "id": "standard-tunneling"
        }, {
          "value": "Requirements",
          "depth": 1,
          "id": "requirements"
        }, {
          "value": "Setup",
          "depth": 1,
          "id": "setup"
        }, {
          "value": "Troubleshooting",
          "depth": 1,
          "id": "troubleshooting"
        }, {
          "value": "Reverse tunneling",
          "depth": 0,
          "id": "reverse-tunneling"
        }, {
          "value": "Requirements",
          "depth": 1,
          "id": "requirements-1"
        }, {
          "value": "Setup",
          "depth": 1,
          "id": "setup-1"
        }, {
          "value": "Troubleshooting",
          "depth": 1,
          "id": "troubleshooting-1"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      h3: "h3",
      ul: "ul",
      li: "li",
      strong: "strong",
      code: "code",
      ol: "ol",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Tunneling lets Hightouch securely open a connection to a data warehouse in your private network or Virtual Private Cloud (VPC) without exposing it to the internet. SSH tunnels are secure, authenticated, encrypted, and dedicated to your workspace. To learn more about SSH tunneling, check out ", _jsx(_components.a, {
          href: "https://www.ssh.com/academy/ssh/tunneling",
          children: "this introductory article"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "This feature is supported for the following:"
      }), "\n", _jsx(_components.h3, {
        id: "sources",
        children: "Sources"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/synapse",
            children: "Azure Synapse"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/clickhouse",
            children: "ClickHouse"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/datawarehouseio",
            children: "Datawarehouse.io"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/greenplum",
            children: "Greenplum"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/metabase",
            children: "Metabase"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/mixpanel",
            children: "Mixpanel"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/mongodb",
            children: "MongoDB"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/mysql",
            children: "MySQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/oracle",
            children: "Oracle DB"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/postgresql",
            children: "PostgreSQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/single-store",
            children: "SingleStore"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/snowflake",
            children: "Snowflake"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/tableau",
            children: "Tableau"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/teradata-vantage",
            children: "Teradata Vantage"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/sources/trino",
            children: "Trino"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "event-sources",
        children: "Event Sources"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/events/sources/kafka",
            children: "Kafka"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "destinations",
        children: "Destinations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/alloydb",
            children: "AlloyDB"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/databricks",
            children: "Databricks"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/elasticsearch",
            children: "Elasticsearch"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/mariadb",
            children: "MariaDB"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/mongodb",
            children: "MongoDB"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/mysql",
            children: "MySQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/opensearch",
            children: "OpenSearch"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/oracle-db",
            children: "Oracle DB"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/postgres",
            children: "PostgreSQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/redis",
            children: "Redis"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/docs/destinations/snowflake",
            children: "Snowflake"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "event-destinations",
        children: "Event Destinations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "PostgreSQL"
        }), "\n", _jsx(_components.li, {
          children: "Redshift"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "standard-versus-reverse-tunneling",
        children: "Standard versus reverse tunneling"
      }), "\n", _jsxs(_components.p, {
        children: ["Both ", _jsx(_components.strong, {
          children: "Standard"
        }), " and ", _jsx(_components.strong, {
          children: "Reverse"
        }), " tunnels open a secure port connection between Hightouch and your data warehouse. However, they differ in implementation, and you may prefer one based on your network specifications."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "#standard-tunneling",
          children: "Standard tunnels"
        }), " require you to run ", _jsx(_components.code, {
          children: "sshd"
        }), " on a ", _jsx(_components.a, {
          href: "https://aws.amazon.com/quickstart/architecture/linux-bastion/",
          children: "bastion host"
        }), " accessible from the public internet. Our systems open an SSH connection to your bastion, then open a port forwarding connection from your bastion to the private service you specify."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "#reverse-tunneling",
          children: "Reverse tunnels"
        }), " let you forward a port by connecting as a client to an SSH server managed by Hightouch. This removes the necessity for a bastion host in your infrastructure but requires you to maintain that connection. That means if your connection goes down for whatever reason, your systems automatically re-open the connection. You can use programs like ", _jsx(_components.a, {
          href: "https://linux.die.net/man/1/autossh",
          children: "`autossh``"
        }), ", ", _jsx(_components.a, {
          href: "https://docs.docker.com/config/containers/start-containers-automatically/",
          children: "Docker container restart policies"
        }), ", or process supervisors like ", _jsx(_components.a, {
          href: "http://supervisord.org/",
          children: "supervisord"
        }), " to help maintain your connection."]
      }), "\n", _jsx(_components.h2, {
        id: "standard-tunneling",
        children: "Standard tunneling"
      }), "\n", _jsx(_components.h3, {
        id: "requirements",
        children: "Requirements"
      }), "\n", _jsx(WhitelistDetails, {
        target: "bastion host"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Allowlist the Hightouch ", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "static IP addresses"
          }), " corresponding to your region."]
        }), "\n", _jsx(_components.li, {
          children: "Allow connections from the bastion host to your warehouse."
        }), "\n", _jsxs(_components.li, {
          children: ["Set up a user on the bastion host named ", _jsx(_components.code, {
            children: "hightouch"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, navigate to ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/tunnels",
          children: [_jsx(_components.strong, {
            children: "Settings"
          }), " > ", _jsx(_components.strong, {
            children: "Tunnel"
          })]
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add Tunnel"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "Name"
          }), " for your tunnel."]
        }), "\n", _jsxs(_components.li, {
          children: ["Fill out the ", _jsx(_components.strong, {
            children: "SSH Host"
          }), " and ", _jsx(_components.strong, {
            children: "SSH Port"
          }), ".", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "These are the connection details for your public-facing bastion server host."
            }), "\n", _jsxs(_components.li, {
              children: ["The port is most likely 22, standard for ", _jsx(_components.code, {
                children: "sshd"
              }), "."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Fill out the ", _jsx(_components.strong, {
            children: "Service Host"
          }), " and ", _jsx(_components.strong, {
            children: "Service Port"
          }), ".", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "These are the connection details for the data warehouse you are connecting to Hightouch."
            }), "\n", _jsxs(_components.li, {
              children: ["Think of your bastion server as a ", _jsx(_components.a, {
                href: "https://en.wikipedia.org/wiki/Jump_server",
                children: "jump host"
              }), ". Hightouch jumps through it to connect to your warehouse using these details."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy or download the generated SSH public key. Add this to the ", _jsx(_components.code, {
            children: "~/.ssh/authorized_keys"
          }), " file for the ", _jsx(_components.code, {
            children: "hightouch"
          }), " user on your bastion server. You can use ", _jsx(_components.a, {
            href: "https://www.ssh.com/academy/ssh/copy-id",
            children: _jsx(_components.code, {
              children: "ssh-copy-id"
            })
          }), " to help with this."]
        }), "\n", _jsxs(_components.li, {
          children: ["The tunnel ", _jsx(_components.strong, {
            children: "Status"
          }), " turns green when the connection is established. Your tunnel is now ready for use."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "troubleshooting",
        children: "Troubleshooting"
      }), "\n", _jsx(_components.p, {
        children: "If you're having trouble establishing a connection with a standard tunnel, check the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Check that you've allowlisted the ", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "static Hightouch IP addresses"
          }), " on your bastion host. See ", _jsx(_components.a, {
            href: "#requirements",
            children: "requirements"
          }), " for more information."]
        }), "\n", _jsxs(_components.li, {
          children: ["Check that the ", _jsx(_components.code, {
            children: "hightouch"
          }), " user exists on your bastion host and that the Hightouch public key is in their ", _jsx(_components.code, {
            children: "~/.ssh/authorized_keys"
          }), " file."]
        }), "\n", _jsxs(_components.li, {
          children: ["Check permissions on the ", _jsx(_components.code, {
            children: "hightouch"
          }), " user's SSH files.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.code, {
                children: "~/.ssh"
              }), " directory should be ", _jsx(_components.code, {
                children: "0700"
              }), "\n", _jsxs(_components.ul, {
                children: ["\n", _jsxs(_components.li, {
                  children: ["Fix: ", _jsx(_components.code, {
                    children: "chmod 0700 /home/hightouch/.ssh"
                  })]
                }), "\n"]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.code, {
                children: "~/.ssh/authorized_keys"
              }), " file should be ", _jsx(_components.code, {
                children: "0644"
              }), "\n", _jsxs(_components.ul, {
                children: ["\n", _jsxs(_components.li, {
                  children: ["Fix: ", _jsx(_components.code, {
                    children: "chmod 0644 /home/hightouch/.ssh/authorized_keys"
                  })]
                }), "\n"]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.code, {
                children: "~/.ssh"
              }), " directory and ", _jsx(_components.code, {
                children: "~/.ssh/authorized_keys"
              }), " file should be owned by the ", _jsx(_components.code, {
                children: "hightouch"
              }), " user", "\n", _jsxs(_components.ul, {
                children: ["\n", _jsxs(_components.li, {
                  children: ["Fix: ", _jsx(_components.code, {
                    children: "chown -R /home/hightouch/.ssh"
                  })]
                }), "\n"]
              }), "\n"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Check that the bastion host can network to your warehouse.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "nc -z $warehouse_host $warehouse_port"
              })
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["If all else fails, ", _jsx(PylonLink, {
            pylonText: "Hello, I'm having an issue setting up an SSH tunnel.",
            linkText: "reach out to our Customer Success team"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "reverse-tunneling",
        children: "Reverse tunneling"
      }), "\n", _jsx(_components.h3, {
        id: "requirements-1",
        children: "Requirements"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "You need a server within your VPC to act as the SSH client."
        }), "\n", _jsx(_components.li, {
          children: "The SSH client server must be able to connect to both the public internet and your warehouse."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "setup-1",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, navigate to ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/tunnels",
          children: [_jsx(_components.strong, {
            children: "Settings"
          }), " > ", _jsx(_components.strong, {
            children: "Tunnel"
          })]
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add reverse tunnel"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "Name"
          }), " for your tunnel."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Download the private key to your machine.", "\n", " ", "\n", _jsx(Warning, {
            children: _jsx(_components.p, {
              children: "Hightouch doesn't store your private key. As a result, we can't recover a\nlost key. Please make a local copy of this key and store it securely."
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy the example ", _jsx(_components.code, {
            children: "ssh"
          }), " command and save it. You'll need to run it later.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Example"
              }), "\n", _jsx(_components.pre, {
                children: _jsxs(_components.code, {
                  className: "hljs language-bash",
                  children: ["ssh -i path/to/key.pem \\\n    -R 0.0.0.0:56000:", _jsx(_components.span, {
                    className: "hljs-variable",
                    children: "$SERVICE_HOST"
                  }), ":", _jsx(_components.span, {
                    className: "hljs-variable",
                    children: "$SERVICE_PORT"
                  }), " \\\n    tunnel.hightouch.io -p 49100 \\\n    -o ExitOnForwardFailure=", _jsx(_components.span, {
                    className: "hljs-built_in",
                    children: "yes"
                  }), "\n"]
                })
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["This command includes the remote Hightouch ", _jsx(_components.code, {
                children: "sshd"
              }), " host and port, and remote forwarding port."]
            }), "\n", _jsxs(_components.li, {
              children: ["Set or replace the ", _jsx(_components.code, {
                children: "$SERVICE_HOST"
              }), " and ", _jsx(_components.code, {
                children: "$SERVICE_PORT"
              }), " variables with your internal warehouse service host and port."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Upload the private key to your SSH client server, store it securely, and ensure its permissions are set to ", _jsx(_components.code, {
            children: "0400"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["From your SSH client server, run the modified ", _jsx(_components.code, {
            children: "ssh"
          }), " command.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["Ensure the ", _jsx(_components.code, {
                children: "-i"
              }), " flag is pointing to the correct path of the private key."]
            }), "\n", _jsxs(_components.li, {
              children: ["You most likely want to wrap this ", _jsx(_components.code, {
                children: "ssh"
              }), " command with a process manager to restart in case of failure. Consider ", _jsx(_components.a, {
                href: "https://linux.die.net/man/1/autossh",
                children: _jsx(_components.code, {
                  children: "autossh"
                })
              }), "."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["The tunnel ", _jsx(_components.strong, {
            children: "Status"
          }), " turns green when the connection is established. Your tunnel is now ready for use."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Hightouch Reverse SSH Host Key"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you prefer, you can check that Hightouch's hostname is correctly set to either ", _jsx(_components.code, {
          children: "tunnel.aws-us-east-1.hightouch.com"
        }), ", ", _jsx(_components.code, {
          children: "tunnel.aws-eu-west-1.hightouch.com"
        }), ", ", _jsx(_components.code, {
          children: "tunnel.aws-ap-south-1.hightouch.com"
        }), ", or ", _jsx(_components.code, {
          children: "tunnel.gcp-us-east4.production.hightouch.com"
        }), " depending on your workspace region and that the Host Key is correctly included in ", _jsx(_components.code, {
          children: "~/.ssh/known_hosts"
        }), " as follows:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABgQCXjpH+c24rYGDBoUdHGN3Yazp4SrDU8Z0Oqb0WRaKJz0VpSPS+Kqmc0MioY3yK1aPDh32VSEFUykEHIobV7dsH9uKlR3OllTkxN9pIk3xfJBn1/x4TS2VX1I+XM7KE6xJH/QmBVaVyNzsYzZ+lR/hUdrVC3czV+ReJa5j+YS23atS3x9NznxHSBBOLcq0RpFj65/WZt0kJwbIqhrGYIq5qXR/8d7gzyCj6vg2iNNUszTFQvcFeQ8zeXC63ke4C9oCFHbneX2odvsDY5DQM6WF7H6j91qRhGEq+niQg2zCns1kJq1Gfa7bY3OxIHmQ3HP0R8ExJMsBBqdKoL5uNY8UK717yNuFzBQUV8fgJi1HZC6JR6uDVpLadbXUV3WD0LA0YQ1h24KKGtPRDrnyZxNTdn0R19asz31npyFHKrP95OIz2RoGuqsn9fr3FzO1wfXjdzYp5l6AAy04K6yPNQfTC/+LLk8U1PWbSsMBqlQnHkp/eMaSdTEVMVxqhAFKCz8M=\n"
        })
      }), "\n", _jsx(_components.h3, {
        id: "troubleshooting-1",
        children: "Troubleshooting"
      }), "\n", _jsx(_components.p, {
        children: "If you're having trouble establishing a connection with a reverse tunnel, check the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Check that your SSH client server is running and can access the public internet."
        }), "\n", _jsxs(_components.li, {
          children: ["Check that you've uploaded the private key and that it's only readable by the user initiating the SSH connection (", _jsx(_components.code, {
            children: "chmod 0400"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Add the ", _jsx(_components.code, {
            children: "-v"
          }), " (verbose) flag to your SSH command to see more detailed errors."]
        }), "\n", _jsxs(_components.li, {
          children: ["If all else fails, ", _jsx(PylonLink, {
            pylonText: "Hello, I'm having an issue setting up a reverse SSH tunnel.",
            linkText: "reach out to our Customer Success team"
          }), ". Please include any errors in the SSH output."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
